import { Center, Flex, Group, Popover, ScrollArea, Stack, Text } from '@mantine/core';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import React, { useMemo } from 'react';
import StatusIndicator from '@/components/StatusIndicator.tsx';
import useRetrieveAutomationRuns from '@/hooks/api/runs/useRetrieveAutomationRuns.ts';
import CustomSpinner from '@/components/Spinner.tsx';
import { flattenInfiniteQueryData } from '@/utils/misc.ts';
import RunHistoryCard from '@/pages/Run/components/RunHistorySidebar/RunHistoryCard.tsx';
import { HiChevronDown } from 'react-icons/hi2';
import { getRunDisplayName, Run } from '@/types/runs.ts';

type props = {
  run: Run;
};

const RunSelectMenu = ({ run }: props) => {
  const runDisplayName = getRunDisplayName(run);

  const {
    data: runs,
    isPending: isLoadingRuns,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useRetrieveAutomationRuns({});

  const allRuns = useMemo(() => flattenInfiniteQueryData(runs), [runs]);

  return (
    <Flex align="center" px="md" h={45} mih={45}>
      <Popover
        position="right-start"
        shadow="lg"
        width={'300px'}
        offset={4}
        closeOnEscape={true}
        trapFocus
      >
        <Popover.Target>
          <StyledButton
            variant={'subtle'}
            size={'md'}
            rightSection={<HiChevronDown color={'#000000'} />}
            styles={{
              root: { flex: 1, maxWidth: 'fit-content', paddingLeft: '8px', paddingRight: '8px' },
              inner: { overflow: 'hidden' },
            }}
            mx={'-4px'}
          >
            <Group gap={'2px'} h={'20px'} wrap={'nowrap'} style={{ overflow: 'hidden' }}>
              <StatusIndicator status={run.status} include_text={false} />
              <Text fw={500} size={'14px'} c={'#0F172A'} truncate={'end'} title={run.name}>
                {runDisplayName}
              </Text>
            </Group>
          </StyledButton>
        </Popover.Target>
        <Popover.Dropdown
          p={0}
          w={300}
          h={400}
          mt={'25px'}
          ml={'-20px'}
          style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}
        >
          <Text size={'12px'} c={'#757575'} mt={'md'} px={'md'} pb={'4px'}>
            All Results
          </Text>
          <Flex w={'100%'} style={{ overflow: 'hidden', minHeight: 0, height: '100%' }}>
            <ScrollArea w={'100%'} h={'100%'} scrollbars={'y'} py={'xs'}>
              {isLoadingRuns ? (
                <Center w={'100%'} mt={'lg'}>
                  <CustomSpinner size={'sm'} />
                </Center>
              ) : (
                <Stack gap={'2px'} w={'100%'} px={'xs'}>
                  {allRuns.map((run) => (
                    <RunHistoryCard key={run.id} run={run} showMenu={false} />
                  ))}
                </Stack>
              )}
              {hasNextPage && (
                <Center w={'100%'} my={'xs'}>
                  <StyledButton
                    variant={'outline'}
                    loading={isFetchingNextPage}
                    onClick={() => fetchNextPage()}
                    size={'sm'}
                  >
                    Load More
                  </StyledButton>
                </Center>
              )}
            </ScrollArea>
          </Flex>
        </Popover.Dropdown>
      </Popover>
    </Flex>
  );
};

export default RunSelectMenu;
